import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import RequestAccess from './pages/RequestAccess';
import Dashboard from './pages/Dashboard';
import Contractors from './pages/Parties/Contractors';
import Providers from './pages/Parties/Providers';
import Assignments from './pages/Parties/Assignments';
import Collections from './pages/Collections/Collections';
import Invoices from './pages/Billing/Invoices';
import Payments from './pages/Billing/Payments';
import Tasks from './pages/Tasks/Tasks';
import Documents from './pages/Documents/Documents';
import Reports from './pages/Reports/Reports';
import Settings from './pages/Settings/Settings';
import Credentials from './pages/Credentialing/Credentials';
import Scheduler from './pages/Scheduler/Scheduler';
import AdminDashboard from './pages/Admin/AdminDashboard';
import Profile from './pages/Profile/Profile';
import Security from './pages/Security/Security';

const theme = createTheme({
  palette: {
    primary: { main: '#3b82f6', light: '#60a5fa', dark: '#2563eb', contrastText: '#fff' },
    secondary: { main: '#8b5cf6', light: '#a78bfa', dark: '#7c3aed' },
    success: { main: '#10b981', light: '#34d399', dark: '#059669' },
    warning: { main: '#f59e0b', light: '#fbbf24', dark: '#d97706' },
    error: { main: '#ef4444', light: '#f87171', dark: '#dc2626' },
    info: { main: '#06b6d4', light: '#22d3ee', dark: '#0891b2' },
    background: { default: '#f1f5f9', paper: '#ffffff' },
    text: { primary: '#0f172a', secondary: '#64748b' },
    divider: '#e2e8f0',
    grey: { 50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 300: '#cbd5e1', 400: '#94a3b8', 500: '#64748b' }
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h4: { fontWeight: 800, letterSpacing: '-0.02em' },
    h5: { fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600, fontSize: '0.8rem' },
    body2: { fontSize: '0.85rem' },
    button: { fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  shadows: [
    'none',
    '0 1px 2px rgba(0,0,0,0.04)',
    '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
    '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -2px rgba(0,0,0,0.03)',
    '0 10px 15px -3px rgba(0,0,0,0.05), 0 4px 6px -4px rgba(0,0,0,0.03)',
    '0 20px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.03)',
    ...Array(19).fill('0 25px 50px -12px rgba(0,0,0,0.15)')
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 10, padding: '8px 20px', fontSize: '0.85rem' },
        contained: { boxShadow: 'none', '&:hover': { boxShadow: '0 4px 12px rgba(59,130,246,0.3)' } },
        outlined: { borderWidth: 1.5, '&:hover': { borderWidth: 1.5 } },
      },
      defaultProps: { disableElevation: true }
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }
      }
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 700, fontSize: '0.72rem', color: '#64748b',
            textTransform: 'uppercase', letterSpacing: '0.06em',
            borderBottom: '2px solid #e2e8f0', padding: '12px 16px',
            background: '#f8fafc'
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: { root: { borderColor: '#f1f5f9', padding: '12px 16px', fontSize: '0.85rem' } }
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:hover': { backgroundColor: '#f8fafc' }, transition: 'background-color 0.15s' }
      }
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, fontSize: '0.75rem', borderRadius: 8 } }
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 16 } }
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontWeight: 700, fontSize: '1.15rem', padding: '20px 24px' } }
    },
    MuiTextField: {
      defaultProps: { size: 'small', variant: 'outlined' },
      styleOverrides: {
        root: { '& .MuiOutlinedInput-root': { borderRadius: 10 } }
      }
    },
    MuiSelect: {
      styleOverrides: { root: { borderRadius: 10 } }
    },
    MuiOutlinedInput: {
      styleOverrides: { root: { borderRadius: 10 } }
    },
    MuiIconButton: {
      styleOverrides: { root: { borderRadius: 10 } }
    },
    MuiAccordion: {
      styleOverrides: { root: { borderRadius: '12px !important', border: '1px solid #e2e8f0', '&:before': { display: 'none' } } }
    },
    MuiTooltip: {
      styleOverrides: { tooltip: { borderRadius: 8, fontSize: '0.75rem' } }
    }
  }
});

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  // If user has 2FA required but hasn't set it up, force them to Security page
  if (user.twoFaRequired && !user.totpEnabled && location.pathname !== '/security') {
    return <Navigate to="/security" replace />;
  }
  // Super admin lands on multi-tenant dashboard by default
  if (user.role === 'super_admin' && (location.pathname === '/' || location.pathname === '/dashboard')) {
    return <Navigate to="/admin" replace />;
  }
  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/request-access" element={<PublicRoute><RequestAccess /></PublicRoute>} />
            <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/contractors" element={<ProtectedRoute><Contractors /></ProtectedRoute>} />
            <Route path="/providers" element={<ProtectedRoute><Providers /></ProtectedRoute>} />
            <Route path="/assignments" element={<ProtectedRoute><Assignments /></ProtectedRoute>} />
            <Route path="/collections" element={<ProtectedRoute><Collections /></ProtectedRoute>} />
            <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
            <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
            <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
            <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/credentialing" element={<ProtectedRoute><Credentials /></ProtectedRoute>} />
            <Route path="/scheduler" element={<ProtectedRoute><Scheduler /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/security" element={<ProtectedRoute><Security /></ProtectedRoute>} />
            <Route path="/users" element={<Navigate to="/settings" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover theme="colored" />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
